import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import {
    CellClassParams,
    ColDef,
    Column,
    ColumnWidthCallbackParams,
    GridOptions,
    IAggFunc,
    ICellRendererParams,
    RowClassParams,
    RowNode,
    RowStyle,
} from 'ag-grid-community';
import * as _ from 'lodash';
import { forEach, get, isArray, isEmpty, isNaN, isNil, isNull, isNumber, isObject, map, some } from 'lodash';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';

import { AgGridUtils } from '@ag-grid-reports/helpers/utils';
import { ClassTypeCellRendererInterface } from '@ag-grid-reports/interfaces/class-type-cell-renderer.interface';
import {
    BUSINESS_DATE_FORMAT,
    CARD_EXPIRATION_DATE_FORMAT,
    DATE_FORMAT,
    DATE_TIME_DETAIL_FORMAT,
    MOMENT_FORMATS,
    TIME_12H_FORMAT,
    TIME_24H_FORMAT,
    TIME_FORMAT,
} from '@config/global-constants';
import { XVITypeReport } from '@containers/reports/report/ag-grid-reports/helpers/report-identificators';
import { add_report, make_default, reset_default } from '@rpt-ngrx/actions/reports.action';
import { FileDownloadService } from '@services/file-download/file-download.service';
import { HelperService } from '@services/helper/helper.service';
import { LoggerService } from '@services/logger/logger.service';
import { ColumnFieldClass, ColumnFieldClassList } from '@services/preferences/preferences.enum';
import { PreferencesService } from '@services/preferences/preferences.service';
import { IEvent, IPublishedReport, IReport, ISubtitle, ReportOptions } from '@shared/types';
import { IEventState } from '@shared/types/event';
import { convertUTCDateTimeByTimeZone, removeInvalidZeros, sumArray } from '@shared/utils';

@Injectable({
    providedIn: 'root',
})
export class AgGridReportsService {
    private totalOrdersAvg = 0;
    private totalGuestsAvg = 0;
    private customAvgAggValue = new Map();
    private popupData: { content1: string; content2: string; columns: string } = null;
    private toastResetEmitter$: Subject<void> = new Subject();
    private totalAverageValues = {
        calculated_avg_transaction: 0,
        calculated_sales_per_labor_hour: 0,
        calculated_guests_per_labor_hour: 0,
    };
    public totalSumValues: { [key: string]: number | string } = {};

    constructor(
        private preferencesService: PreferencesService,
        private helperService: HelperService,
        private store: Store,
        private translations: TranslateService,
        private fileDownloadService: FileDownloadService,
        private loggerService: LoggerService,
    ) {}

    static isDateTypeOfData(value: string): boolean {
        return moment(value, MOMENT_FORMATS, true).isValid();
    }

    public updateTotalAverageValues = (key: string, value: string | number) => {
        this.totalAverageValues[key] = value;
    };

    public roundValue(data): string {
        const value = isObject(data) ? data.value : data;
        return Number(value).toFixed(1).toString();
    }

    public getTotalSumValues = () => this.totalSumValues;

    public setTotalSumValues = (key: string, value: string | number) => {
        this.totalSumValues[key] = value;
    };

    public toPercentage(data): string {
        return (data.value * 100).toFixed(2).toString() + '%';
    }

    public customNumberComparator(valueA, valueB) {
        if ((valueA === '(None)' && valueB === '(None)') || (valueA === null && valueB === null)) {
            return 0;
        }
        if (valueA === '(None)' || valueA === null) {
            return -1;
        }
        if (valueB === '(None)' || valueB === null) {
            return 1;
        }
        return Number(valueA) - Number(valueB);
    }

    public customBooleanComparator(valueA: boolean | string, valueB: boolean | string): number {
        return Number(Boolean(valueB)) - Number(Boolean(valueA));
    }

    public customTimeComparator(valueA, valueB) {
        if (moment(valueA, TIME_FORMAT).isAfter(moment(valueB, TIME_FORMAT))) {
            return -1;
        } else {
            return 1;
        }
    }

    public customTimeIntervalComparatorByEod(a, b, eod) {
        if (typeof a !== 'string' || typeof b !== 'string') {
            return;
        }
        const timeA = moment(a.split(' - ')[0], TIME_12H_FORMAT);
        const timeB = moment(b.split(' - ')[0], TIME_12H_FORMAT);
        const comparand = moment(eod, TIME_24H_FORMAT);

        if (timeA.isSame(comparand)) {
            return -1;
        } else if (timeB.isSame(comparand)) {
            return 1;
        } else if (timeA.isBefore(comparand) && timeB.isAfter(comparand)) {
            return 1;
        } else if (timeB.isBefore(comparand) && timeA.isAfter(comparand)) {
            return -1;
        } else {
            return timeA.isBefore(timeB) ? -1 : 1;
        }
    }

    public customTimeStringComparatorByEod(a, b, eod) {
        if (!a || !b) {
            return;
        }

        const timeA = moment(a, DATE_TIME_DETAIL_FORMAT);
        const timeB = moment(b, DATE_TIME_DETAIL_FORMAT);
        const comparand = moment(eod, DATE_TIME_DETAIL_FORMAT);

        if (timeA.isSame(comparand)) {
            return -1;
        } else if (timeB.isSame(comparand)) {
            return 1;
        } else if (timeA.isBefore(comparand) && timeB.isAfter(comparand)) {
            return 1;
        } else if (timeB.isBefore(comparand) && timeA.isAfter(comparand)) {
            return -1;
        } else {
            return timeA.isBefore(timeB) ? -1 : 1;
        }
    }

    public customDateIntervalComparator(a, b) {
        if (typeof a !== 'string' || typeof b !== 'string') {
            return;
        }
        const dateA = moment(a.split(' - ')[0], BUSINESS_DATE_FORMAT);
        const dateB = moment(b.split(' - ')[0], BUSINESS_DATE_FORMAT);

        return dateA.isBefore(dateB) ? -1 : 1;
    }

    public customCardExpirationDateComparator(a: string, b: string) {
        if (a === b) return 0;

        if (a === '') {
            return 1;
        }

        if (b === '') {
            return -1;
        }

        const dateA = moment(a, CARD_EXPIRATION_DATE_FORMAT);
        const dateB = moment(b, CARD_EXPIRATION_DATE_FORMAT);

        return dateA.isBefore(dateB) ? -1 : 1;
    }

    public customStringComparator(valueA: string, valueB: string): number {
        if ((valueA === '(None)' && valueB === '(None)') || (isNil(valueA) && isNil(valueB))) {
            return 0;
        }
        if (valueA === '(None)' || isNil(valueA)) {
            return -1;
        }
        if (valueB === '(None)' || isNil(valueB)) {
            return 1;
        }
        return valueA?.localeCompare(valueB);
    }

    public customStringComparatorCaseInsensitive(valueA: string, valueB: string): number {
        if ((valueA === '(None)' && valueB === '(None)') || (isNil(valueA) && isNil(valueB))) {
            return 0;
        }
        if (valueA === '(None)' || isNil(valueA)) {
            return -1;
        }
        if (valueB === '(None)' || isNil(valueB)) {
            return 1;
        }
        return valueA?.toLowerCase().localeCompare(valueB?.toLowerCase());
    }

    public numberFormatter = (params) => {
        return this.preferencesService.formatToNumber(params);
    };

    public currencyFormatter = (params, type = 'grid') => {
        return this.preferencesService.currencyFormatterForAgGrid(params, type);
    };

    public customCurrencyFormatterForAgGridWithEmptyValues = (params) => {
        return this.preferencesService.customCurrencyFormatterForAgGridWithEmptyValues(params);
    };

    public stringCellRenderer = (params, i18nKey = '') => {
        return this.preferencesService.stringCellFormatter(params, i18nKey, this.translations);
    };

    public booleanStringCellRenderer = (params) => {
        return this.preferencesService.booleanStringCellFormatter(params, this.translations);
    };

    public numberCellRenderer = (params, precision = null, type = 'grid') => {
        return this.preferencesService.numberFormatterForAgGrid(params, precision, type);
    };

    public floatNumberCellRenderer = (params, precision = null, type = 'grid') => {
        return this.preferencesService.floatFormatterForAgGrid(params, precision, false, type);
    };

    public percentageCellRenderer = (params, precision = null, type = 'grid') => {
        return this.preferencesService.percentsFormatterForAgGrid(params, precision, type);
    };

    public customerNameCellRenderer = (params) => {
        return this.preferencesService.customerCellFormatter(params, this.translations);
    };

    public dateCellRenderer = (renderParams, format?: string) => {
        const params = isObject(renderParams) ? renderParams : { value: renderParams.value };
        if (params.value === undefined || params.value === null) {
            return '';
        }
        if (params.value === '(None)') {
            return this.translations.instant('NONE');
        } else {
            return this.preferencesService.dateFormatter(params.value, this.translations, format);
        }
    };

    public businessDateRenderer = (params): string => {
        return this.preferencesService.businessDateFormatter(params, this.translations);
    };

    public splitCombineCellRenderer = (params): string => {
        const value = isObject(params) ? params.value : params;

        if (!value) {
            return '';
        }
        return this.translations.instant(value);
    };

    public dateAndTimeCellRenderer = (renderParams, formatDate?: string, formatTime?: any) => {
        return this.preferencesService.dateAndTimeFormatter(renderParams, this.translations, {
            dateFormat: formatDate,
            timeFormat: formatTime,
        });
    };

    public timeCellRenderer = (renderParams) => {
        return this.preferencesService.timeFormatter(renderParams, this.translations);
    };

    public durationCellRenderer = (renderParams) => {
        return this.preferencesService.formatToDuration(renderParams);
    };

    public customFooterValueGetter(params) {
        const totalForLabel = this.translations.instant('TOTAL_FOR');
        let filterColumn = {};
        if (params.value) {
            let value = AgGridReportsService.isDateTypeOfData(params.value)
                ? moment(params.value).locale(this.preferencesService.timeLocale).format('L')
                : params.value;

            switch (value) {
                case '(None)':
                case 'None':
                    value = this.translations.instant('NONE');
                    break;
                case 'N/A':
                    value = this.translations.instant('N/A');
                    break;
            }

            const columnDefs = params.column?.columnApi?.columnModel?.columnDefs;
            if (!_.isEmpty(columnDefs)) {
                columnDefs.forEach((column) => {
                    filterColumn = _.find(column.children, {
                        headerName: params.colDef.headerName,
                        enableValueFormat: true,
                    });
                });
                if (!_.isEmpty(filterColumn)) {
                    const formatter = _.get(filterColumn, 'valueFormatter');
                    return totalForLabel + ' ' + formatter(value);
                }
            }
            return totalForLabel + ' ' + this.translations.instant(value);
        } else {
            return totalForLabel;
        }
    }

    public customRowStyles = (params: RowClassParams): RowStyle | undefined => {
        if (isNumber(+params?.node?.id)) {
            const rowNode = params.api.getRowNode(params.node.id);
            const nextRowNode = params.api.getRowNode(+params.node.id + 1 + '');
            const prevRowNode = params.api.getRowNode(+params.node.id - 1 + '');

            if (this.isNearbyRowsWithSameValuesForColumn(rowNode, nextRowNode)) {
                return { background: 'var(--light-green-color)' };
            }

            if (this.isNearbyRowsWithSameValuesForColumn(rowNode, prevRowNode)) {
                return {
                    background: 'var(--light-green-color)',
                    'border-bottom': '1px solid var(--extra-gray-color)',
                };
            }
        }

        return {};
    };

    public customSumAggFunc = (arr) => {
        if (arr?.values?.length) {
            const sum = this.preferencesService.correctRounding(this.sum(arr.values), 2);
            this.totalSumValues[arr.colDef.field] = sum;
            return sum;
        } else if (arr?.length) {
            const sum = this.preferencesService.correctRounding(this.sum(arr));
            this.totalSumValues[arr.colDef.field] = sum;
            return sum;
        } else {
            this.totalSumValues[arr.colDef.field] = 0;
            return 0;
        }
    };

    public customAvgAggFunction: IAggFunc = (params) => {
        let result = 0;
        switch (params.colDef.field) {
            case 'orders_avg':
                result += this.getTotalAverageValue(+this.totalSumValues.net_sales, +this.totalSumValues.orders);
                this.totalAverageValues['orders_avg'] = result;
                if (params.rowNode.level === -1 && this.totalOrdersAvg === 0) {
                    this.totalOrdersAvg = result;
                }
                break;
            case 'guest_avg':
                result += this.getTotalAverageValue(+this.totalSumValues.net_sales, +this.totalSumValues.guests);
                this.totalAverageValues['guest_avg'] = result;
                if (params.rowNode.level === -1 && this.totalGuestsAvg === 0) {
                    this.totalGuestsAvg = result;
                }
                break;
            case 'calculated_avg_transaction':
                result += this.getTotalAverageValue(+this.totalSumValues.net_sales, +this.totalSumValues.total_orders);
                this.totalAverageValues['calculated_avg_transaction'] = result;
                if (params.rowNode.level === -1 && this.totalGuestsAvg === 0) {
                    this.totalGuestsAvg = result;
                }
                break;
            case 'calculated_sales_per_labor_hour':
                result += this.getTotalAverageValue(+this.totalSumValues.net_sales, +this.totalSumValues.labor_hours);
                this.totalAverageValues['calculated_sales_per_labor_hour'] = result;
                break;
            case 'calculated_guests_per_labor_hour':
                result += this.getTotalAverageValue(+this.totalSumValues.guests, +this.totalSumValues.labor_hours);
                this.totalAverageValues['calculated_guests_per_labor_hour'] = result;
                break;
            case 'tip_percentage':
                result +=
                    this.getTotalAverageValue(
                        +this.totalSumValues.sum_tip_total,
                        +this.totalSumValues.sum_order_total,
                    ) * 100;
                this.totalAverageValues['tip_percentage'] = result;
                break;
            default:
                result += params.values.reduce((acc, item) => acc + Number.parseFloat(item), 0) / params.values.length;
                this.customAvgAggValue.set(params.colDef.field, result);
        }
        return result;
    };

    public getTotalOrdersAvg() {
        return this.totalOrdersAvg;
    }

    public getTotalGuestsAvg() {
        return this.totalGuestsAvg;
    }

    public getCustomAvgValue(id) {
        return this.customAvgAggValue.get(id);
    }

    public getTotalAverageValues(id) {
        return this.totalAverageValues[id];
    }

    public areEqualDates(dates: any[]): boolean {
        if (some(dates, '(None)')) {
            return false;
        }
        return moment(dates[0]).isSame(dates[1], 'day');
    }

    public generateDisplayConflictsPopupContent(options): void {
        const { filters, content1, content2 } = options;
        const length = filters.length;
        let columns = '';

        switch (length) {
            case 1:
                columns = `${filters[0]} ${this.translations.instant('COLUMN')}`;
                break;
            case 2:
                columns = `${filters[0]}, ${filters[1]} ${this.translations.instant('COLUMNS')}`;
                break;
            default:
                columns =
                    `${filters[0]}, ${filters[1]} ` +
                    ` ${this.translations.instant('AND')} +${filters.length - 2} ${this.translations.instant('OTHER_COLUMNS')}`;
                break;
        }

        this.popupData = {
            content1,
            content2,
            columns,
        };
    }

    public getPopupData() {
        return this.popupData;
    }

    public toastResetListener(): Observable<void> {
        return this.toastResetEmitter$.asObservable();
    }

    public isDateValueInField(item, value): boolean {
        return (
            isArray(item?.userProvidedColDef?.cellClass) &&
            item?.userProvidedColDef?.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_DATE) >= 0 &&
            moment(value).format() !== 'Invalid date'
        );
    }

    public prepareCurrencyField(field: string, isRefund = false): string {
        if (isNull(field) || isEmpty(field)) {
            return '0.0000';
        }
        let value = field;
        if (isRefund) {
            value = `${Math.abs(+field) * -1}`;
        }
        const index = value.indexOf('.');
        return value.slice(0, index + 5);
    }

    public prepareXviUnitField(field: string): string {
        const reg = /(.*)\[(.*)\]/;

        if (!reg.test(field)) {
            return '';
        }

        const execRes = reg.exec(field);

        return `${execRes[1]}[${this.preferencesService.xviFormatToFloatNumber(Number(execRes[2]))}]`;
    }

    public isXVITypeReport(reportId): boolean {
        return XVITypeReport.includes(reportId);
    }

    /**
     * Converts a 2-D string array to a CSV file and triggers a browser download.
     * Uses the injected DOCUMENT token for testability and SSR safety.
     */
    public downloadCsvContent(rows: string[][], fileName: string): void {
        if (!rows.length) {
            return;
        }
        const csvContent = rows
            .map((row) =>
                row
                    .map((cell) => {
                        const str = String(cell ?? '');
                        return /[,"\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
                    })
                    .join(','),
            )
            .join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        this.fileDownloadService.saveAs(blob, fileName);
    }

    public exportReportToCSV(
        gridOptions: GridOptions,
        reportTitle: string = 'Report',
        translations: TranslateService,
        reportId: string = '',
        subTitles: Array<string | { text: string; class: string; alignment: string }>,
    ) {
        const isXVIReport = this.isXVITypeReport(reportId);
        const subTitlesText = subTitles.map((title) => '"' + title + '"').join(' ');

        // Check if this is a Master/Detail report
        const isMasterDetailReport = gridOptions.masterDetail === true;

        const params = {
            allColumns: false,
            onlySelected: false,
            skipFooters: true,
            skipGroups: false,
            skipColumnHeaders: isMasterDetailReport, // For master-detail reports, we generate them in getCustomContentBelowRow
            skipPinnedBottom: true,
            skipPinnedTop: true,
            suppressQuotes: false,
            fileName: `${reportTitle}.csv`,
            columnSeparator: ',',
            skipColumnGroupHeaders: isXVIReport || isMasterDetailReport,
            columnWidth: 10,
            skipRowGroups: true,
            processHeaderCallback: (values) => {
                return this.processHeaderCallback(values, translations, isXVIReport);
            },
            processCellCallback: (item) => {
                if (item.column.colDef.showRowGroup && isNil(item.value)) {
                    item.value = get(item.node.data, item.column.colDef.showRowGroup, null);
                }

                // For Master/Detail CSV, skip master row content since it's handled by getCustomContentBelowRow
                if (isMasterDetailReport) {
                    return ''; // Skip master row content - detail content comes from getCustomContentBelowRow
                }

                return this.processCSVCellCallback(item, translations);
            },
            prependContent: isXVIReport ? subTitlesText : '',
            // For Master/Detail reports, use component's getCustomContentBelowRow callback
            getCustomContentBelowRow:
                isMasterDetailReport &&
                (gridOptions as GridOptions & { getCustomContentBelowRow?: (params: RowNode) => unknown[][] })
                    .getCustomContentBelowRow
                    ? (params) => {
                          try {
                              // Add CSV context to params so component knows this is CSV export
                              const csvParams = { ...params, exportType: 'csv' };
                              const result = (
                                  gridOptions as GridOptions & {
                                      getCustomContentBelowRow?: (params: RowNode) => unknown[][];
                                  }
                              ).getCustomContentBelowRow(csvParams);

                              // ag-Grid CSV expects CsvCell format
                              if (Array.isArray(result)) {
                                  const formatted = result.map((row) => {
                                      if (Array.isArray(row)) {
                                          return row.map((cell) => {
                                              let cellValue: string;
                                              if (typeof cell === 'object' && cell !== null && 'value' in cell) {
                                                  cellValue = String(cell.value || '');
                                              } else {
                                                  cellValue = String(cell || '');
                                              }
                                              // Return CsvCell format
                                              return { data: { value: cellValue, type: 'String' } };
                                          });
                                      }
                                      return [{ data: { value: String(row || ''), type: 'String' } }];
                                  });
                                  return formatted;
                              }
                              return [];
                          } catch (error) {
                              console.error(this.translations.instant('AG_GRID.CSV_EXPORT_ERROR'), error);
                              return [];
                          }
                      }
                    : undefined,
        };

        gridOptions.api.exportDataAsCsv(params);

        this.logExport('CSV', reportTitle, reportId);
    }

    public exportReportToXLS(
        gridOptions: GridOptions,
        reportTitle: string = 'Report',
        translations: TranslateService,
        reportId: string = '',
        subTitles: Array<string | { text: string; class: string; alignment: string }>,
    ) {
        const colsNames = this.getExportColumnNames(gridOptions);
        const isXVIReport = this.isXVITypeReport(reportId);
        const columnsToExport: Column[] = gridOptions.columnApi.getAllDisplayedColumns();
        const { groupIncludeTotalFooter, groupIncludeFooter } = gridOptions;

        // Check if this is a Master/Detail report
        const isMasterDetailReport = gridOptions.masterDetail === true;

        const prependData = () => {
            const prependCells = subTitles.map((item) => [
                {
                    data: {
                        value: item,
                        type: 'String',
                    },
                    styleId: 'subHeader',
                },
            ]);

            prependCells.push([]);

            return prependCells;
        };

        const params = {
            allColumns: false,
            columnGroups: true,
            onlySelected: false,
            skipFooters: true,
            skipGroups: false,
            skipHeader: false,
            skipPinnedBottom: true,
            skipPinnedTop: true,
            suppressQuotes: true,
            fileName: `${reportTitle}.xlsx`,
            sheetName: name,
            columnKeys: colsNames,
            skipColumnGroupHeaders: isXVIReport,
            exportedRows: 'filteredAndSorted',
            rowGroupExpandState: 'match',
            // When detail rows have more cells than master columns, those extra cells fall
            // into Excel columns with no width definition → they collapse to 0.58 (Excel min).
            // The callback is invoked per column; `column` is null for extra columns.
            columnWidth: isMasterDetailReport
                ? (params: ColumnWidthCallbackParams) => params.column?.getActualWidth() ?? 100
                : undefined,
            processHeaderCallback: (values) => {
                return this.processHeaderCallback(values, translations, isXVIReport);
            },
            processCellCallback: (item) => {
                const currentField = get(item, 'column.colId', '');
                const isGrouped = get(item, 'node.group', false);
                const hasAggData = get(item, 'node.aggData', null);

                if (isGrouped && currentField && !isNil(hasAggData?.[currentField]) && item.node.expanded) {
                    return '';
                }
                return this.processCellCallback(item, translations);
            },
            shouldRowBeSkipped: ({ node }) => {
                return !node.displayed;
            },
            getCustomContentBelowRow: ({ node }) => {
                let totalContent = [];
                const detailContent = [];

                if (groupIncludeFooter || groupIncludeTotalFooter) {
                    totalContent = [
                        ...this.getTotalInfoForGroupedRow(
                            node,
                            columnsToExport,
                            groupIncludeFooter,
                            groupIncludeTotalFooter,
                        ),
                    ];
                }

                if (isMasterDetailReport && !node.group && (gridOptions as any).getCustomContentBelowRow) {
                    const masterDetailContent = (gridOptions as any).getCustomContentBelowRow({
                        node: node,
                        // XLS export has no detail-column selector, so undefined means
                        // "use all detail columns" in report-specific callbacks.
                        detailColumns: undefined,
                    });

                    if (masterDetailContent && masterDetailContent.length > 0) {
                        const excelFormattedContent = masterDetailContent.map((row) => {
                            const isHeaderRow = row.length > 0 && (row[0] as any)?.isHeader === true;
                            const styleId = isHeaderRow ? 'detailHeader' : 'detailRow';
                            const indentCell = { data: { type: 'String', value: '' }, styleId };
                            return [
                                indentCell,
                                ...row.map((cell) => ({
                                    data: {
                                        type: 'String',
                                        value: typeof cell === 'object' && cell.value ? cell.value : cell,
                                    },
                                    styleId,
                                })),
                            ];
                        });
                        detailContent.push(...excelFormattedContent);
                    }
                }

                // In master/detail exports, details must appear before totals so
                // the grand total remains the final row in the worksheet.
                return [...detailContent, ...totalContent];
            },
            prependContent: isXVIReport ? prependData() : '',
        };

        gridOptions.api.exportDataAsExcel(params as any);

        this.logExport('XLSX', reportTitle, reportId);
    }

    public getTotalInfoForGroupedRow(
        node: RowNode,
        columnsToExport: Column[],
        groupIncludeFooter: boolean,
        groupIncludeTotalFooter: boolean,
    ) {
        const result: any[] = [];
        let parentNode = node.parent;

        while (!isEmpty(parentNode)) {
            // In master/detail grids, expanding the last master row sets node.expanded=true
            // (to show detail rows). That should not suppress the grand-total footer in XLS.
            const canRenderTotalForCurrentNode = node.lastChild && (!node.expanded || !!node.master);
            if (canRenderTotalForCurrentNode) {
                const totalRow = [];
                const parenIsRootNode = parentNode.level === -1;

                if ((!parenIsRootNode && groupIncludeFooter) || (parenIsRootNode && groupIncludeTotalFooter)) {
                    forEach(columnsToExport, (column, index) => {
                        const parenIsRootNode = parentNode.level === -1;

                        if ((index === parentNode.level && !parenIsRootNode) || (parenIsRootNode && !index)) {
                            const cellFormatter =
                                parentNode?.rowGroupColumn?.['colDef']?.cellRenderer ||
                                parentNode?.rowGroupColumn?.['colDef']?.valueFormatter;
                            let groupKey: string;
                            if (cellFormatter && typeof cellFormatter === 'function' && !isNil(parentNode.key)) {
                                groupKey = cellFormatter(parentNode.key)?.toString();
                            } else {
                                groupKey = parentNode.key;
                            }
                            totalRow.push({
                                data: {
                                    type: 'String',
                                    value: parenIsRootNode
                                        ? this.translations.instant('TOTAL')
                                        : `${this.translations.instant('TOTAL_FOR')} ${groupKey ? this.translations.instant(groupKey) : ''}`,
                                },
                                styleId: 'totalLine',
                            });
                        } else {
                            const cellRenderer =
                                (column as any).colDef.cellRenderer || (column as any).colDef.valueFormatter;
                            const aggData = get(parentNode, 'aggData', null);
                            let value = !isNil(aggData?.[column.getColId()]) ? aggData?.[column.getColId()] : null;
                            if (cellRenderer && typeof cellRenderer === 'function' && !isNil(value)) {
                                const doc = new DOMParser().parseFromString(cellRenderer(value), 'text/html');
                                const isHtml = [].slice.call(doc.body.childNodes).some((node) => node.nodeType === 1);
                                const element = document.createElement('div');
                                const isClass = cellRenderer.prototype !== undefined;
                                if (isClass) {
                                    const renderer: ICellRendererAngularComp & ClassTypeCellRendererInterface =
                                        new cellRenderer(this.translations);
                                    if (typeof (<ClassTypeCellRendererInterface>renderer).returnValue !== 'function') {
                                        throw new Error(this.translations.instant('AG_GRID.CELL_RENDERER_ERROR'));
                                    }
                                    renderer.agInit({ node, value } as ICellRendererParams);
                                    element.innerHTML = renderer.returnValue();
                                    value = isHtml ? element.innerText : renderer.returnValue();
                                } else {
                                    element.innerHTML = cellRenderer(value);
                                    value = isHtml ? element.innerText : cellRenderer(value)?.toString();
                                }
                            }
                            totalRow.push({
                                data: {
                                    type: 'String',
                                    value,
                                },
                                styleId: 'totalLine',
                            });
                        }
                    });
                    result.push(totalRow);
                }
            }

            if (parentNode.lastChild) {
                parentNode = parentNode.parent;
            } else {
                parentNode = {} as any;
            }
        }

        return result;
    }

    public addCustomBottomLabel(name: string, rowData: RowNode[] | unknown[]): void {
        const curElem = [...Array.from(document.querySelectorAll<HTMLElement>('.ag-custom-label'))];

        if (curElem) {
            curElem.forEach((item, index) => {
                if (index === 0) {
                    item.hidden = true;
                } else {
                    item.remove();
                }
            });
        }

        if (!rowData?.length) {
            return;
        }

        setTimeout(() => {
            const tempElem: HTMLElement = document.querySelector('.ag-center-cols-clipper');
            const heightElem = tempElem.style.height.slice(0, -2);
            if (curElem.length === 0) {
                const elem = document.querySelector('.ag-body-viewport');
                const label = document.createElement('span');

                label.style.top = +heightElem - 28 + 'px';
                label.classList.add('ag-custom-label');
                label.innerHTML = name;

                elem.appendChild(label);
            } else {
                curElem[0].hidden = false;
                curElem[0].style.top = +heightElem - 25 + 'px';
            }
        }, 100);
    }

    public addClickableStyling(params: CellClassParams, ignoreNone = true, ignoreNA = true) {
        const checkNo1 = ignoreNone ? params.value !== '(None)' : true;
        const checkNo2 = ignoreNA ? params.value !== 'N/A' : true;
        const customStyle = {
            textDecoration: 'underline',
            textDecorationColor: parseFloat(params.value) < 0 ? 'red' : 'inherit',
            cursor: 'pointer',
        };

        if (params.data && params.value && checkNo1 && checkNo2) {
            return customStyle;
        }

        return null;
    }

    private sum(array): number {
        return array.reduce((acc, item) => (acc += Number.parseFloat(item)), 0);
    }

    public isNearbyRowsWithSameValuesForColumn(firstRow, secondRow, columnName: string = 'order_number'): boolean {
        const rowsAreNotGroups = !firstRow?.group && !secondRow?.group;
        if (rowsAreNotGroups) {
            const rowsHaveSameOrderId = firstRow?.data.order_id === secondRow?.data.order_id;
            const rowsHaveSameValues = firstRow?.data[columnName] === secondRow?.data[columnName];
            const rowsAreNeighbours =
                firstRow?.childIndex === secondRow?.childIndex + 1 ||
                firstRow?.childIndex === secondRow?.childIndex - 1;
            return rowsHaveSameOrderId && rowsHaveSameValues && rowsAreNeighbours;
        }
        return false;
    }

    public getExportColumnNames(gridOptions: GridOptions): string[] {
        const columnsToShow = [];
        const allColumns: Column[] = gridOptions.columnApi.getAllGridColumns();
        for (let i = 0; i < allColumns.length; i++) {
            if (allColumns[i].isVisible()) {
                columnsToShow.push(allColumns[i].getColId());
            }
        }

        return columnsToShow;
    }

    private getAdditionalGroupData(item, extra_data = ''): string {
        const coldef = item.column?.colDef;
        if (
            coldef.cellRenderer === 'agGroupCellRenderer' &&
            coldef.cellRendererParams?.context?.columnGroupField === coldef.showRowGroup
        ) {
            coldef.cellRendererParams.context.extraColumnNamesToInject.forEach((column) => {
                const originalColDef = item.api.getColumnDefs().find((col) => col.field === column.colDefField);
                const fieldValue = item.node?.aggData?.[column.colDefField] ?? item.node?.data?.[column.colDefField];

                extra_data +=
                    `\n${this.translations.instant(column.name)}: ` +
                    `${this.preferencesService.getFormatterByClass(originalColDef.cellClass)(fieldValue)}`;
            });
            return extra_data;
        }

        return '';
    }

    private processCellCallback(cellParams: ICellRendererParams, translations: TranslateService): string {
        let value = '';
        let groupExtraInfo = '';
        let cellRenderer = cellParams.column.getColDef().cellRenderer || cellParams.column.getColDef().valueFormatter;
        if (cellParams.column.getColDef().showRowGroup) {
            groupExtraInfo = this.getAdditionalGroupData(cellParams);
            cellRenderer =
                cellParams.columnApi.getColumn(cellParams.column.getColDef().showRowGroup).getColDef().cellRenderer ||
                cellParams.columnApi.getColumn(cellParams.column.getColDef().showRowGroup).getColDef().valueFormatter;
        }

        value = this.prepareCellValue(cellRenderer, cellParams);

        if (value === '(None)') {
            return translations.instant('NONE');
        }

        if (!value) {
            groupExtraInfo = '';
        }

        const data = value && typeof value === 'string' ? translations.instant(value) : value;

        return data + groupExtraInfo;
    }

    private processCSVCellCallback(cellParams: ICellRendererParams, translations: TranslateService): string {
        let value = '';
        let groupExtraInfo = '';
        let cellRenderer = cellParams.column.getColDef().cellRenderer || cellParams.column.getColDef().valueFormatter;
        let isNumberColumn = false;
        if (cellParams.column.getColDef().showRowGroup) {
            groupExtraInfo = this.getAdditionalGroupData(cellParams);
            // use column's cellRenderer instead of general one
            cellRenderer =
                cellParams.columnApi.getColumn(cellParams.column.getColDef().showRowGroup).getColDef().cellRenderer ||
                cellParams.columnApi.getColumn(cellParams.column.getColDef().showRowGroup).getColDef().valueFormatter;
        }

        value = this.prepareCellValue(cellRenderer, cellParams);

        if (value === '(None)') {
            return translations.instant('NONE');
        }

        if (!value) {
            groupExtraInfo = '';
        }
        let cellClass = get(cellParams, 'column.colDef.cellClass', []);
        if (typeof cellClass !== 'function') {
            isNumberColumn = false;
        } else {
            cellClass = cellClass(cellParams);
        }

        isNumberColumn = cellClass.some((cssClass) => ColumnFieldClassList.includes(cssClass));

        if (Number(cellParams.value) < 0 && isNumberColumn && cellRenderer && typeof cellRenderer === 'function') {
            const isNegativeWithBracket = value.indexOf('(') !== -1;
            value = isNegativeWithBracket ? `-${value.replace(/\(|\)|（|）/g, '')}` : value;
        }

        if (isArray(cellParams.value) && isNumberColumn && cellRenderer && typeof cellRenderer === 'function') {
            const formattedArr = value.split(' - ');
            value = map(formattedArr, (item) => {
                const isNegativeWithBracket = item.indexOf('(') !== -1;
                return isNegativeWithBracket ? `-${item.replace(/\(|\)|（|）/g, '')}` : item;
            }).join(' - ');
        }

        const data = value && typeof value === 'string' ? translations.instant(value) : value;

        return data + groupExtraInfo;
    }

    private prepareCellValue<
        T extends (new (args: TranslateService) => ICellRendererAngularComp & ClassTypeCellRendererInterface) &
            ((arg: object) => string),
    >(cellRenderer: T, cellParams: ICellRendererParams): string {
        if (cellRenderer && typeof cellRenderer === 'function') {
            const isClass = <ICellRendererAngularComp>cellRenderer.prototype !== undefined;
            let renderedValue = '';
            let params = cellParams;
            if (
                (cellParams.column.getColDef() as ColDef & { context?: string | string[] }).context?.includes(
                    'row-specified-type',
                )
            ) {
                params = AgGridUtils.prepareRowSpecifiedCellParams(
                    cellParams.column.getColDef().field,
                    cellParams.node.data,
                ) as unknown as ICellRendererParams;
            }

            if (isClass) {
                const renderer: ClassTypeCellRendererInterface & ICellRendererAngularComp = new cellRenderer(
                    this.translations,
                );
                if (typeof (<ClassTypeCellRendererInterface>renderer).returnValue !== 'function') {
                    throw new Error(this.translations.instant('AG_GRID.CELL_RENDERER_ERROR'));
                }
                renderer.agInit({ node: cellParams.node, value: cellParams.value } as ICellRendererParams);
                renderedValue = renderer.returnValue() || '';
            } else {
                renderedValue = cellRenderer(params);
            }
            const doc = new DOMParser().parseFromString(renderedValue, 'text/html');
            const isHtml = [].slice.call(doc.body.childNodes).some((node) => node.nodeType === 1);
            const element = document.createElement('div');

            element.innerHTML = renderedValue;
            return isHtml ? element.innerText : renderedValue;
        } else {
            return cellParams.value;
        }
    }

    private processHeaderCallback(item, translations: TranslateService, isXVIReport: boolean): string {
        if (isXVIReport) {
            const column = item.column;
            const colDef = column.getColDef();
            if (colDef && colDef.headerName) {
                const groupHeader = column.getParent() && column.getParent().getColGroupDef()?.headerName;
                const columnHeader = translations.instant(colDef.headerName);
                const combinedHeader = groupHeader
                    ? `${translations.instant(groupHeader)} ${columnHeader}`
                    : columnHeader;
                return combinedHeader;
            }
        }

        return translations.instant(item.column.getColDef().headerName);
    }

    public getTotalAverageValue(...values): number {
        return +values[0] === 0
            ? 0
            : +values[1] === 0
              ? 0
              : this.preferencesService.correctRounding(+values[0]) /
                this.preferencesService.correctRounding(+values[1]);
    }

    public xviReportsSumAggFunc = (params): number | string => {
        const defaultValue = 0;
        const { calculationPrecision, quantityPrecision } =
            this.preferencesService.inventoryCurrencyAndNumbersPreferences;
        const currencyPrecision = !isNaN(Number(calculationPrecision))
            ? Number(calculationPrecision)
            : this.preferencesService.currencyAndNumbersPreferences.precision;
        const cellClassString = params.colDef.cellClass.toString();
        const isCurrencyField =
            cellClassString.indexOf(ColumnFieldClass.CUSTOM_XVI_PRICE) >= 0 ||
            cellClassString.indexOf(ColumnFieldClass.CUSTOM_XVI_ARROW_FLOAT) >= 0;
        const precision = isCurrencyField ? currencyPrecision : quantityPrecision;
        if (params?.values?.length) {
            const correctRoundedValue = this.preferencesService.correctRounding(sumArray(params.values), precision);
            const value = removeInvalidZeros(
                String(correctRoundedValue),
                this.preferencesService.currencyAndNumbersPreferences.decimalSeparator,
                !isCurrencyField ? 0 : 2,
            );
            return (this.totalSumValues[params.colDef.field] = value);
        } else if (params?.length) {
            const sum = this.preferencesService.correctRounding(sumArray(params), precision);
            return (this.totalSumValues[params.colDef.field] = sum);
        } else {
            return (this.totalSumValues[params.colDef.field] = defaultValue);
        }
    };

    public xcatGetSelectedEventState(eventState: IEventState, report: IReport, timezone): ISubtitle[] {
        let selectedEventDate = '';
        let selectedEventCount = 0;
        let selectedEventsNames: string[] = [];
        let event: IEvent | null = null;
        const { selectedEvents, selectedEvent } = eventState;
        const multiple = report?.additional_path_parameters.filter((item) => item.type === 'events')[0]?.multiple;

        if (multiple) {
            if (selectedEvents?.length === 1) {
                event = selectedEvents[0];
            }
        } else {
            event = selectedEvent ? selectedEvent : null;
        }

        if (event && event.event_days) {
            const { start_date_time } = event.event_days[0];

            selectedEventDate = convertUTCDateTimeByTimeZone(start_date_time, timezone, DATE_FORMAT);
        } else {
            selectedEventDate = '';
        }

        selectedEventCount = multiple ? selectedEvents?.length : selectedEvent ? 1 : 0;
        selectedEventsNames = multiple ? selectedEvents.map((evt) => evt.name) : [event.name];

        if (selectedEventCount > 0) {
            if (selectedEventCount === 1) {
                return [
                    {
                        id: 'event',
                        name: this.translations.instant('Event'),
                        value: selectedEventsNames[0],
                    },
                    {
                        id: 'event_date',
                        name: this.translations.instant('EVENT_DATE'),
                        value: selectedEventDate,
                    },
                ];
            } else {
                return [
                    {
                        id: 'event_count',
                        name: this.translations.instant('EVENT_COUNT'),
                        value: `${selectedEventCount}`,
                    },
                ];
            }
        } else {
            return [];
        }
    }

    public getFrozenFilters(
        reportUrl: ReportOptions,
        report: IReport | IPublishedReport,
    ): Array<{ [key: string]: number }> {
        const additionalPathParameters = report?.additional_path_parameters || [];

        return additionalPathParameters.reduce((acc, filter) => {
            if (
                filter.attributes.includes('Freeze_For_Published') ||
                filter.attributes.includes('Freeze_Permanently')
            ) {
                const attr = filter.attributes.find((attr) => Object.keys(reportUrl).includes(attr));
                acc[filter.name] = attr ? reportUrl[attr] : null;
            }
            return acc;
        }, {});
    }

    public publishReport({
        gridColumnApi,
        gridApi,
        reportUrl,
        report,
        currentCompanyId,
        permissions,
        params = null,
        extraGridOptions = {},
    }): void {
        // Preserve base publish behavior even when master/detail injects extra options.
        // These core.ts fields must always come from the live master grid state.
        const {
            columns: _ignoredColumns,
            filterModel: _ignoredFilterModel,
            frozenFilters: _ignoredFrozenFilters,
            ...safeExtraGridOptions
        } = (extraGridOptions || {}) as {
            columns?: ColDef[];
            filterModel?: { [key: string]: any };
            frozenFilters?: Array<{ [key: string]: number }>;
            [key: string]: any;
        };

        const gridOptions: {
            columns: ColDef[];
            filterModel: { [key: string]: any };
            frozenFilters: Array<{ [key: string]: number }>;
            [key: string]: any;
        } = {
            columns: gridColumnApi.getColumnState().map((column: ColDef) => ({
                aggFunc: column.aggFunc,
                colId: column.colId,
                flex: null,
                hide: column.hide,
                pinned: column.pinned,
                pivot: false,
                pivotIndex: null,
                rowGroup: column.rowGroup,
                rowGroupIndex: column.rowGroupIndex,
                sort: column.sort,
                sortIndex: column.sortIndex,
                width: column.width,
            })),
            filterModel: gridApi.getFilterModel(),
            frozenFilters: this.getFrozenFilters(reportUrl, report) || [],
            ...safeExtraGridOptions,
        };
        this.store.dispatch(
            add_report({
                companyId: currentCompanyId,
                gridOptions,
                parentReport: report,
                permissions: permissions,
                detailPageOptions: params?.detailPageOptions || null,
            }),
        );
    }

    /**
     * @param extraGridOptions  Optional extra properties merged into gridOptions before saving.
     *                          Used by master/detail reports to include detailGridOptions.
     *                          Ignored by regular reports (defaults to {}).
     */
    public makeDefaultSchema({ currentCompanyId, gridColumnApi, gridApi, report, extraGridOptions = {} }): void {
        const gridOptions: {
            columns: ColDef[];
            filterModel: { [key: string]: any };
            [key: string]: any;
        } = {
            columns: gridColumnApi.getColumnState().map((column: ColDef) => ({
                aggFunc: column.aggFunc,
                colId: column.colId,
                flex: null,
                hide: column.hide,
                pinned: column.pinned,
                pivot: false,
                pivotIndex: null,
                rowGroup: column.rowGroup,
                rowGroupIndex: column.rowGroupIndex,
                sort: column.sort,
                sortIndex: column.sortIndex,
                width: column.width,
            })),
            filterModel: gridApi.getFilterModel(),
            ...extraGridOptions,
        };
        this.store.dispatch(
            make_default({
                companyId: currentCompanyId,
                gridOptions,
                report,
            }),
        );
    }

    public resetDefaultSchema({ currentCompanyId, report }): void {
        this.store.dispatch(
            reset_default({
                companyId: currentCompanyId,
                report,
            }),
        );
    }

    public logExport(exportFormat: 'CSV' | 'XLSX' | 'PDF', reportName: string, reportId: string): void {
        this.loggerService.logExport(exportFormat, reportName, reportId);
    }
}
